import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { TrendingUp, TrendingDown, Download, AlertTriangle, ChevronUp, ChevronDown } from 'lucide-react';
import financialService from '../../services/financialService';
import { useFinancialRefresh } from '../../contexts/FinancialRefreshContext';

const DEFAULT_BALANCE_SUMMARY = {
  totalRevenue: 0,
  totalExpenses: 0,
  netProfit: 0,
  profitMargin: 0,
  operatingRatio: 0,
  costPerMile: 0,
  change: {
    revenue: 0,
    expenses: 0,
    netProfit: 0,
    profitMargin: 0,
    operatingRatio: 0,
    costPerMile: 0
  }
};

const formatPeriodLabel = period => {
  if (!period) return 'Unknown';
  const [year, month] = period.split('-');
  const date = new Date(Number(year), Number(month) - 1, 1);
  if (Number.isNaN(date.getTime())) return period;
  return date.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
};

const getMonthAbbreviation = period => {
  if (!period) return 'Unknown';
  const [year, month] = period.split('-');
  const date = new Date(Number(year), Number(month) - 1, 1);
  if (Number.isNaN(date.getTime())) return period;
  return date.toLocaleDateString(undefined, { month: 'short' });
};

const calculateChange = (current = 0, previous = 0) => {
  if (!previous || previous === 0) return 0;
  return ((current - previous) / Math.abs(previous)) * 100;
};

// Helper to get current and previous month date ranges
const getDateRanges = () => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();

  // Current month dates
  const currentStart = new Date(currentYear, currentMonth, 1).toISOString().slice(0, 10);
  const currentEnd = new Date(currentYear, currentMonth + 1, 0).toISOString().slice(0, 10);

  // Previous month dates
  const previousStart = new Date(currentYear, currentMonth - 1, 1).toISOString().slice(0, 10);
  const previousEnd = new Date(currentYear, currentMonth, 0).toISOString().slice(0, 10);

  return {
    current: { startDate: currentStart, endDate: currentEnd },
    previous: { startDate: previousStart, endDate: previousEnd }
  };
};

const OverviewTab = ({ formatCurrency }) => {
  const [companyId, setCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const trucksPerPage = 10;
  const { refreshTriggers } = useFinancialRefresh();

  // Truck performance data from API
  const [truckPerformance, setTruckPerformance] = useState([]);
  const [truckLoading, setTruckLoading] = useState(true);
  const [truckError, setTruckError] = useState(null);

  // KPI data from API
  const [kpiData, setKpiData] = useState(null);
  const [kpiLoading, setKpiLoading] = useState(true);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const storedCompany = window.localStorage.getItem('company');
      if (storedCompany) {
        const parsed = JSON.parse(storedCompany);
        setCompanyId(parsed?.id || null);
      }
    } catch (err) {
      console.warn('Unable to parse company from localStorage', err);
    }
  }, []);

  // Fetch truck performance data from API
  const fetchTruckPerformance = useCallback(async () => {
    if (!companyId) {
      setTruckPerformance([]);
      setTruckLoading(false);
      return;
    }

    setTruckLoading(true);
    setTruckError(null);

    try {
      const dateRanges = getDateRanges();
      const trucks = await financialService.getTruckPerformance({
        startDate: dateRanges.current.startDate,
        endDate: dateRanges.current.endDate
      });

      setTruckPerformance(trucks || []);
    } catch (err) {
      console.error('Failed to load truck performance:', err);
      setTruckError('Unable to load truck performance data.');
      setTruckPerformance([]);
    } finally {
      setTruckLoading(false);
    }
  }, [companyId]);

  // Fetch KPI data for current month
  const fetchKPIData = useCallback(async () => {
    if (!companyId) {
      setKpiLoading(false);
      return;
    }

    setKpiLoading(true);

    try {
      const dateRanges = getDateRanges();
      const overviewData = await financialService.getOverviewData({
        startDate: dateRanges.current.startDate,
        endDate: dateRanges.current.endDate,
        previousStartDate: dateRanges.previous.startDate,
        previousEndDate: dateRanges.previous.endDate
      });

      setKpiData(overviewData);
    } catch (err) {
      console.error('Failed to load KPI data:', err);
      setKpiData(null);
    } finally {
      setKpiLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;
    fetchTruckPerformance();
    fetchKPIData();
  }, [companyId, fetchTruckPerformance, fetchKPIData]);

  // Refresh data when triggered by financial changes
  useEffect(() => {
    if (!companyId) return;
    if (refreshTriggers.overview > 0 || refreshTriggers.all > 0) {
      // Debounce refresh to avoid multiple rapid updates
      const timer = setTimeout(() => {
        fetchTruckPerformance();
        fetchKPIData();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [refreshTriggers.overview, refreshTriggers.all, companyId, fetchTruckPerformance, fetchKPIData]);

  // Summary data from API
  const summary = useMemo(() => {
    // Use KPI data from the API
    if (kpiData?.currentPeriod) {
      return {
        totalRevenue: kpiData.currentPeriod.totalRevenue,
        totalExpenses: kpiData.currentPeriod.totalExpenses,
        netProfit: kpiData.currentPeriod.netProfit,
        profitMargin: kpiData.currentPeriod.profitMargin,
        operatingRatio: kpiData.currentPeriod.operatingRatio,
        costPerMile: kpiData.currentPeriod.costPerMile,
        change: {
          revenue: kpiData.changes.revenue,
          expenses: kpiData.changes.expenses,
          netProfit: kpiData.changes.netProfit,
          profitMargin: kpiData.changes.profitMargin,
          operatingRatio: kpiData.changes.operatingRatio,
          costPerMile: kpiData.changes.costPerMile
        }
      };
    }

    // Return default if no API data
    return DEFAULT_BALANCE_SUMMARY;
  }, [kpiData]);

  // Use real truck data or empty array
  const truckData = useMemo(() => {
    return truckPerformance.length > 0 ? truckPerformance : [];
  }, [truckPerformance]);

  const sortedTrucks = useMemo(() => {
    let sortableData = [...truckData];
    if (sortConfig.key !== null) {
      sortableData.sort((a, b) => {
        let aValue, bValue;

        // Handle nested data structure
        switch (sortConfig.key) {
          case 'truck':
            aValue = a.vehicleNumber || a.vehicleId || a.truck || a.id || '';
            bValue = b.vehicleNumber || b.vehicleId || b.truck || b.id || '';
            break;
          case 'revenue':
            aValue = a.financial?.revenue || a.revenue || 0;
            bValue = b.financial?.revenue || b.revenue || 0;
            break;
          case 'expense':
            aValue = a.financial?.expenses || a.expense || 0;
            bValue = b.financial?.expenses || b.expense || 0;
            break;
          case 'profit':
            aValue = a.financial?.profit || a.profit || 0;
            bValue = b.financial?.profit || b.profit || 0;
            break;
          case 'operatingRatio':
            aValue = a.financial?.operatingRatio || a.operatingRatio || 0;
            bValue = b.financial?.operatingRatio || b.operatingRatio || 0;
            break;
          default:
            aValue = a[sortConfig.key] || 0;
            bValue = b[sortConfig.key] || 0;
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableData;
  }, [sortConfig, truckData]);

  const paginatedTrucks = useMemo(() => {
    const startIndex = (currentPage - 1) * trucksPerPage;
    return sortedTrucks.slice(startIndex, startIndex + trucksPerPage);
  }, [sortedTrucks, currentPage, trucksPerPage]);

  const totalPages = Math.ceil(sortedTrucks.length / trucksPerPage);

  const KPICard = ({ title, value, change, isPercentage, color = 'blue' }) => {
    const isPositive = change >= 0;
    const changeColor = title === 'Cost Per Mile' ? (isPositive ? 'text-red-600' : 'text-green-600') : (isPositive ? 'text-green-600' : 'text-red-600');
    const Icon = isPositive ? TrendingUp : TrendingDown;

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 transition-all duration-200 hover:shadow-md">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">{title}</p>
            <p className={`text-2xl font-bold ${color === 'blue' ? 'text-blue-600' : color === 'green' ? 'text-green-600' : 'text-gray-900'}`}>
              {isPercentage ? `${value.toFixed(1)}%` : formatCurrency(value)}
            </p>
          </div>
          <div className={`flex items-center space-x-1 ${changeColor}`}>
            <Icon className="w-4 h-4" />
            <span className="text-sm font-medium">
              {Math.abs(change).toFixed(1)}%
            </span>
          </div>
        </div>
      </div>
    );
  };


  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const exportTruckDataToCSV = () => {
    const headers = ['Truck #', 'Revenue', 'Expense', 'Profit', 'Operating Ratio'];
    const csvContent = [
      headers.join(','),
      ...truckData.map(truck => {
        const revenue = truck.financial?.revenue || truck.revenue || 0;
        const expenses = truck.financial?.expenses || truck.expense || 0;
        const profit = truck.financial?.profit || truck.profit || 0;
        const operatingRatio = truck.financial?.operatingRatio || truck.operatingRatio || 0;
        const truckId = truck.vehicleNumber || truck.vehicleId || truck.truck || truck.id;

        return [
          truckId,
          revenue.toFixed(2),
          expenses.toFixed(2),
          profit.toFixed(2),
          operatingRatio.toFixed(1)
        ].join(',');
      })
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'truck-performance.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) {
      return <ChevronUp className="w-4 h-4 text-gray-400" />;
    }
    return sortConfig.direction === 'asc' ?
      <ChevronUp className="w-4 h-4 text-gray-600" /> :
      <ChevronDown className="w-4 h-4 text-gray-600" />;
  };

  const getOperatingRatioColor = (ratio) => {
    if (ratio < 95) return 'text-green-600 bg-green-50';
    if (ratio <= 100) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  if (kpiLoading || truckLoading) {
    return (
      <div className="space-y-6">
        {/* Loading KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse">
              <div className="h-4 bg-gray-200 rounded mb-3"></div>
              <div className="h-8 bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>

        {/* Loading Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse">
          <div className="h-6 bg-gray-200 rounded mb-4 w-1/3"></div>
          <div className="h-80 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-red-200 p-6">
        <div className="flex items-center justify-between text-red-700">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="w-5 h-5" />
            <p>{error}</p>
          </div>
          <button type="button" className="text-sm font-medium underline" onClick={fetchKPIData}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!kpiData) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <p className="text-sm text-gray-600">No financial data available yet. Once transactions start posting we will show your KPIs here automatically.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <KPICard
          title="Revenue This Month"
          value={summary.totalRevenue}
          change={summary.change.revenue}
          color="blue"
        />
        <KPICard
          title="Operating Ratio"
          value={summary.operatingRatio}
          change={summary.change.operatingRatio}
          isPercentage
          color={summary.operatingRatio < 95 ? 'green' : 'red'}
        />
        {summary.costPerMile > 0 && (
          <KPICard
            title="Cost Per Mile"
            value={summary.costPerMile}
            change={summary.change.costPerMile}
          />
        )}
        <KPICard
          title="Net Profit"
          value={summary.netProfit}
          change={summary.change.netProfit}
          color="green"
        />
      </div>

      {/* Fleet Performance Table */}
      {truckData.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Fleet Performance</h3>
                <p className="text-sm text-gray-600 mt-1">Current month performance by truck</p>
              </div>
              <button
                onClick={exportTruckDataToCSV}
                className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
              >
                <Download className="w-4 h-4" />
                <span>Export CSV</span>
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  {[
                    { key: 'truck', label: 'Truck #' },
                    { key: 'revenue', label: 'Revenue' },
                    { key: 'expense', label: 'Expense' },
                    { key: 'profit', label: 'Profit' },
                    { key: 'operatingRatio', label: 'Operating Ratio' }
                  ].map(({ key, label }) => (
                    <th
                      key={key}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors duration-200"
                      onClick={() => handleSort(key)}
                    >
                      <div className="flex items-center space-x-1">
                        <span>{label}</span>
                        <SortIcon column={key} />
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedTrucks.map((truck) => {
                  // Extract values from nested structure
                  const revenue = truck.financial?.revenue || truck.revenue || 0;
                  const expenses = truck.financial?.expenses || truck.expense || 0;
                  const profit = truck.financial?.profit || truck.profit || 0;
                  const operatingRatio = truck.financial?.operatingRatio || truck.operatingRatio || 0;
                  const truckId = truck.vehicleNumber || truck.vehicleId || truck.truck || truck.id;

                  return (
                    <tr key={truck.vehicleId || truck.id} className="hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {truckId}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(revenue)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-medium">
                        {formatCurrency(expenses)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">
                        {formatCurrency(profit)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getOperatingRatioColor(operatingRatio)}`}>
                          {operatingRatio.toFixed(1)}%
                        </span>
                      </td>
                    </tr>
                  );
                })}

                {/* Totals Row */}
                <tr className="bg-gray-100 border-t-2 border-gray-300">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                    TOTALS
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                    {formatCurrency(truckData.reduce((sum, truck) => sum + (truck.financial?.revenue || truck.revenue || 0), 0))}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-red-600">
                    {formatCurrency(truckData.reduce((sum, truck) => sum + (truck.financial?.expenses || truck.expense || 0), 0))}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-green-600">
                    {formatCurrency(truckData.reduce((sum, truck) => sum + (truck.financial?.profit || truck.profit || 0), 0))}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-bold rounded-full bg-blue-100 text-blue-800">
                      {(() => {
                        const totalRevenue = truckData.reduce((sum, truck) => sum + (truck.financial?.revenue || truck.revenue || 0), 0);
                        const totalExpenses = truckData.reduce((sum, truck) => sum + (truck.financial?.expenses || truck.expense || 0), 0);
                        const ratio = totalRevenue > 0 ? (totalExpenses / totalRevenue) * 100 : 0;
                        return ratio.toFixed(1);
                      })()}%
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-3 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing {((currentPage - 1) * trucksPerPage) + 1} to {Math.min(currentPage * trucksPerPage, sortedTrucks.length)} of {sortedTrucks.length} results
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200 transition-colors duration-200"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-700">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200 transition-colors duration-200"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* No truck data message */}
      {truckLoading && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-200 rounded mb-4 w-1/3"></div>
            <div className="h-40 bg-gray-200 rounded"></div>
          </div>
        </div>
      )}

      {truckError && (
        <div className="bg-white rounded-lg shadow-sm border border-yellow-200 p-6">
          <div className="flex items-center space-x-2 text-yellow-700">
            <AlertTriangle className="w-5 h-5" />
            <p className="text-sm">{truckError}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default OverviewTab;
